import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { AlertController, NavController } from "@ionic/angular";
import { BobToastService } from "src/app/shared/toast";
import { TranslateService } from "@ngx-translate/core";
import { AppService } from "src/app/_services/app.service";
import { AuthService } from "src/app/_services/auth.service";
import {
  FirebaseAuthService,
  FirebaseAppleSignInPayload,
  FirebaseGoogleSignInPayload,
} from "src/app/_services/firebase/firebase-auth.service";
import { PushNotificationService } from "src/app/_services/firebase/push-notification.service";
import { LocalStorageService } from "src/app/_services/localStorage.service";
import { MessagesService } from "src/app/_services/messages.service";
import { NavigationService } from "src/app/_services/navigation-service.service";
import { osService } from "src/app/_services/osService.service";
import { PusherService } from "src/app/_services/pusher.service";
import { AccountCompletionOrchestratorService } from "src/app/_services/account-completion-orchestrator.service";
import { BackgroundLocationService } from "src/app/_services/background-location.service";
import { SessionStorageService } from "src/app/_services/session-storage.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "app-login",
  templateUrl: "./login.page.html",
  styleUrls: ["./login.page.scss"],
})
export class LoginPage implements OnInit, OnDestroy {
  loginForm: FormGroup;
  textType = "password";
  submitted: boolean = false;
  isLoading: boolean = false;
  emailError: boolean = false;
  emailErrorMessage: string = "";
  passwordError: boolean = false;
  passwordErrorMessage: string = "";
  role: any;
  rememberMe: boolean = false;
  platform!: string;
  savedAccounts: any[] = [];
  loadingConnectAccount: boolean = false;
  loadingGoogle = false;
  loadingApple = false;
  private appStateListener?: { remove: () => Promise<void> };

  constructor(
    public navCtrl: NavController,
    public formBuilder: FormBuilder,
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private osService: osService,
    private router: Router,
    private bobToast: BobToastService,
    private alertController: AlertController,
    private messagesService: MessagesService,
    private appService: AppService,
    private pusherService: PusherService,
    private pushNotificationService: PushNotificationService,
    private navigationService: NavigationService,
    private sessionStorageService: SessionStorageService,
    private translate: TranslateService,
    private firebaseAuth: FirebaseAuthService,
    private accountCompletionOrchestrator: AccountCompletionOrchestratorService,
    private backgroundLocationService: BackgroundLocationService
  ) {
    this.platform = this.appService.getPlatform();
    this.loginForm = this.formBuilder.group({
      email: ["", [Validators.required, this.customEmailValidator]],
      password: ["", Validators.required],
    });
  }
  async ngOnInit(): Promise<void> {
    this.checkForSessionExpiredMessage();

    if (Capacitor.isNativePlatform()) {
      this.appStateListener = await CapacitorApp.addListener(
        "appStateChange",
        (state) => {
          if (state.isActive) {
            void this.redirectAuthenticatedUserFromLogin();
          }
        }
      );
    }
  }

  ngOnDestroy(): void {
    void this.appStateListener?.remove();
  }

  async ionViewWillEnter() {
    const accounts = await this.authService.loadAccounts();
    this.savedAccounts = accounts;
    const redirected = await this.redirectAuthenticatedUserFromLogin();
    if (redirected) {
      return;
    }
    await this.resumeWebGoogleRedirectIfNeeded();
  }

  /** Web: finish Google sign-in after full-page redirect (avoids COOP breaking the popup). */
  private async resumeWebGoogleRedirectIfNeeded(): Promise<void> {
    if (Capacitor.getPlatform() !== "web") {
      return;
    }
    if (this.loadingGoogle || this.isLoading || this.loadingConnectAccount) {
      return;
    }
    const payload = await this.firebaseAuth.consumeWebGoogleRedirectSignIn();
    if (!payload) {
      return;
    }
    this.loadingGoogle = true;
    this.submitGoogleLoginPayload(payload);
  }

  private checkForSessionExpiredMessage(): void {
    // Vérifier si l'utilisateur a été redirigé à cause d'une session expirée
    const queryParams = this.router.url.split('?')[1];
    if (queryParams) {
      const params = new URLSearchParams(queryParams);
      const reason = params.get('reason');
      const message = params.get('message');

      if (reason === 'session_expired' && message) {
        void this.bobToast.errorBottom(message, {
          title: this.translate.instant("BOB_TOAST.LOGIN_ERROR_TITLE"),
          duration: 5000,
        });

        // Nettoyer l'URL
        this.router.navigate(['/login'], {
          replaceUrl: true,
          queryParams: {}
        });
      }
    }
  }

  private async redirectAuthenticatedUserFromLogin(): Promise<boolean> {
    if (!this.isCurrentRouteLogin()) {
      return false;
    }

    const userData = this.localStorageService.getUserData();
    if (userData.client && userData.token) {
      const role = this.normalizeRole(
        this.localStorageService.getUserRole() || userData.client?.roles?.[0]
      );
      this.navigateWithReplace(this.resolvePostLoginRoute(role));
      this.accountCompletionOrchestrator.scheduleCompletionGates();
      return true;
    }

    const restoredRole = await this.restoreNativeAuthSession();
    if (restoredRole) {
      this.navigateWithReplace(this.resolvePostLoginRoute(restoredRole));
      this.accountCompletionOrchestrator.scheduleCompletionGates();
      return true;
    }

    return false;
  }

  private isCurrentRouteLogin(): boolean {
    const path = this.router.url.split("?")[0];
    return path === "/" || path === "/login";
  }

  private async restoreNativeAuthSession(): Promise<string> {
    const nativeAuth = await this.localStorageService.getNativeAuthData();
    if (!nativeAuth.token) {
      return "";
    }

    let user = nativeAuth.user;
    const decodedUserId = this.extractUserIdFromToken(nativeAuth.token);
    const userId = nativeAuth.userId || decodedUserId;
    let role = this.normalizeRole(
      nativeAuth.role || this.getFirstRoleName(user) || "client"
    );

    if (!user && userId) {
      try {
        const response: any = await firstValueFrom(this.appService.getUserById(userId));
        user = response?.body ?? null;
      } catch (_) {
        user = null;
      }
    }

    if (!user && userId) {
      user = {
        id: userId,
        roles: [role],
        current_state: false,
      };
    }

    if (!user) {
      return "";
    }

    role = this.normalizeRole(nativeAuth.role || this.getFirstRoleName(user) || role);
    const normalizedUser = {
      ...user,
      roles: Array.isArray(user.roles)
        ? user.roles.map((r: any) => (typeof r === "string" ? r : r?.name)).filter(Boolean)
        : [role],
      // Normalisation canonique du current_state. Logique précédente couvrait
      // 0/1 (number) mais pas les strings "0"/"1" qui sont truthy en JS et
      // tombaient dans la branche `?? false` → "0" devient "0" (string) →
      // truthy → user marqué online dans l'app alors que backend dit offline.
      current_state: PusherService.normalizeCurrentState(user.current_state),
    };

    this.localStorageService.saveAuthDataFromLogin(
      nativeAuth.token,
      normalizedUser,
      role,
      nativeAuth.refresh
    );

    return role;
  }

  private getFirstRoleName(user: any): string {
    const role = user?.roles?.[0];
    return typeof role === "string" ? role : role?.name ?? "";
  }

  private extractUserIdFromToken(token: string): string {
    try {
      const payload = token.split(".")[1];
      if (!payload) {
        return "";
      }
      const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
      const paddedPayload =
        normalizedPayload + "=".repeat((4 - (normalizedPayload.length % 4)) % 4);
      const decoded = JSON.parse(atob(paddedPayload));
      return String(
        decoded.user_id ??
        decoded.id ??
        decoded.sub ??
        decoded.model_id ??
        ""
      );
    } catch (_) {
      return "";
    }
  }
  customEmailValidator(control: FormControl) {
    const value = control.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!value) {
      return { required: true };
    } else if (!emailRegex.test(value)) {
      return { invalid: true };
    }

    return null;
  }
  checkPassword() {
    let passwordControlErr = this.loginForm.controls["password"];

    passwordControlErr.errors
      ? (this.passwordError = true)
      : (this.passwordError = false);
    if (passwordControlErr) {
      this.f["password"].hasError("wrongPassword")
        ? (this.passwordErrorMessage =
          "Incorrect ! Veuillez saisir un mot de passe valide.")
        : this.f["password"].hasError("required")
          ? (this.passwordErrorMessage =
            "Champ obligatoire ! Veuillez saisir un mot de passe valide.")
          : "";
    }
  }
  checkEmail() {
    let emailControlErr = this.loginForm.controls["email"];
    emailControlErr.errors
      ? (this.emailError = true)
      : (this.emailError = false);
    if (emailControlErr) {
      this.f["email"].hasError("wrongPassword")
        ? (this.emailErrorMessage =
          "Veuillez saisir une adresse e-mail valide.")
        : this.f["email"].hasError("required")
          ? (this.emailErrorMessage =
            "Champ obligatoire ! Veuillez saisir une adresse e-mail valide.")
          : this.f["email"].hasError("userNotFound")
            ? (this.emailErrorMessage =
              "E-mail n'existe pas ! Veuillez saisir une adresse e-mail valide.")
            : this.f["email"].hasError("invalid")
              ? (this.emailErrorMessage =
                "Invalid ! Veuillez saisir une adresse e-mail valide.")
              : "";
    }
  }

  sendLocation(location: any, userId: number) {
    let payload: any = {
      lat: location.coords.latitude.toString(),
      lng: location.coords.longitude.toString(),
      user_id: userId,
    };
    this.osService.saveClientPosition(payload).subscribe();
  }
  login(email: string = "", password: string = "") {
    this.submitted = true;
    this.isLoading = true;
    const formData = email && password ? {email, password} : this.loginForm.value;
    if (this.loginForm.invalid && !email && !password) {
      this.checkPassword();
      this.checkEmail();
      this.isLoading = false;
      return;
    }
    
    this.authService.login(formData).subscribe(
      async (res: any) => {
        await this.finishLogin(res);
        this.isLoading = false;        
        this.hideLoadingAccounts();
        if(this.rememberMe){
          this.authService.addAccount(this.loginForm.value.email, this.loginForm.value.password,res.body.user.image, res.body.user.first_name,res.body.user.last_name);
        }
      },
      (errors: HttpErrorResponse) => {
        this.isLoading = false;   
        let errorMessage = errors.error.error;
        switch (errorMessage) {
          case "wrong password":
            this.loginForm.get("password")!.setErrors({ wrongPassword: true });
            this.passwordError = true;
            this.passwordErrorMessage =
              "Incorrect ! Veuillez saisir un mot de passe valide";
              this.hideLoadingAccounts(this.passwordErrorMessage);
            break;

          case "user not found":
            this.loginForm.get("email")!.setErrors({ userNotFound: true });
            this.emailError = true;
            this.emailErrorMessage =
              "E-mail n'existe pas ! Veuillez saisir une adresse e-mail valide.";
              this.hideLoadingAccounts(this.emailErrorMessage);
            break;
          case "Login failed. User is inactive.":
            this.loginForm.get("email")!.setErrors({ userNotFound: true });
            this.emailError = true;
            this.emailErrorMessage = "Votre compte est actuellement inactif.";
            this.hideLoadingAccounts(this.emailErrorMessage);
            break;
          case "domaine inactive":
            void this.bobToast.errorBottom(
              "bobExpress ne prendra pas en charge votre domaine.",
              {
                title: this.translate.instant("BOB_TOAST.LOGIN_ERROR_TITLE"),
                duration: 5000,
              }
            );
            break;
          default:
            this.loginForm.get("email")!.setErrors({ userNotFound: true });
            this.emailError = true;
            this.emailErrorMessage =
              "Veuillez saisir une adresse e-mail valide.";
            this.hideLoadingAccounts(this.emailErrorMessage);
            break;
        }
        
        if (errors.status !== 401 && errors.status !== 403) {
          console.error("An error occurred:", errors.error);
        }
      }
    );
  }
  hideLoadingAccounts(error= ''){
    this.loadingConnectAccount = false;
    this.savedAccounts.forEach((account: any) => {
      if(account.loading){
        account.loading = false;
        if(error.length){
          void this.bobToast.errorBottom(error, {
            title: this.translate.instant("BOB_TOAST.LOGIN_ERROR_TITLE"),
            duration: 5000,
          });
          
        }
      }
    });
  }

  togglePasswordVisibility() {
    this.textType = this.textType === "text" ? "password" : "text";
  }

  get f() {
    return this.loginForm.controls;
  }

  public async signInWithApple(): Promise<void> {
    if (this.loadingApple || this.isLoading || this.loadingConnectAccount) {
      return;
    }
    this.loadingApple = true;
    try {
      const payload = await this.firebaseAuth.signInWithApple();
      if (!payload) {
        this.loadingApple = false;
        await this.presentAppleSignInError();
        return;
      }
      this.submitAppleLoginPayload(payload);
    } catch (error: unknown) {
      if (this.firebaseAuth.isUserCancelledAppleSignIn(error)) {
        this.loadingApple = false;
        return;
      }
      this.loadingApple = false;
      console.error("Apple sign-in error:", error);
      void this.firebaseAuth.signOut();
      await this.presentAppleSignInError(error);
    }
  }

  private submitAppleLoginPayload(payload: FirebaseAppleSignInPayload): void {
    this.authService
      .loginBySocial("apple", this.buildSocialLoginBody(payload))
      .subscribe({
        next: async (res: any) => {
          this.loadingApple = false;
          try {
            await this.finishLogin(res);
          } finally {
            void this.firebaseAuth.signOut();
          }
        },
        error: (err: unknown) => {
          console.error("Error during Apple social login:", err);
          this.loadingApple = false;
          void this.firebaseAuth.signOut();
          void this.presentAppleSignInError(err);
        },
      });
  }

  /** Same contract as Google for `login-apple` / `login-google` (`google_token` + `user`). */
  private buildSocialLoginBody(
    payload: FirebaseAppleSignInPayload | FirebaseGoogleSignInPayload
  ): { google_token: string; user: FirebaseAppleSignInPayload["profile"] } {
    return {
      google_token: payload.idToken,
      user: payload.profile,
    };
  }

  private extractSocialLoginErrorCode(err: unknown): string | null {
    if (!(err instanceof HttpErrorResponse)) {
      return null;
    }
    const body = err.error as { error?: { code?: string } } | null;
    return body?.error?.code ?? null;
  }

  private async presentAppleSignInError(err?: unknown): Promise<void> {
    const code = err ? this.extractSocialLoginErrorCode(err) : null;
    const messageKey =
      code === "firebase_uid_mismatch"
        ? "LOGIN_PAGE.APPLE_SIGNIN_USE_GOOGLE"
        : "LOGIN_PAGE.APPLE_SIGNIN_FAILED";
    await this.bobToast.errorBottom(this.translate.instant(messageKey), {
      title: this.translate.instant("BOB_TOAST.LOGIN_ERROR_TITLE"),
      duration: code === "firebase_uid_mismatch" ? 7000 : 5000,
    });
  }

  private submitGoogleLoginPayload(payload: FirebaseGoogleSignInPayload): void {
    this.authService
      .loginBySocial("google", this.buildSocialLoginBody(payload))
      .subscribe({
        next: async (res: any) => {
          this.loadingGoogle = false;
          try {
            await this.finishLogin(res);
          } catch (error) {
            console.error("[Login] finishLogin after Google sign-in failed:", error);
            await this.presentGoogleSignInError();
          } finally {
            void this.firebaseAuth.signOut();
          }
        },
        error: (err: any) => {
          console.error("Error during social login:", err);
          this.loadingGoogle = false;
          void this.firebaseAuth.signOut();
          void this.presentGoogleSignInError();
        },
      });
  }

  public async signInWithGoogle(): Promise<void> {
    if (this.loadingGoogle || this.isLoading || this.loadingConnectAccount) {
      return;
    }
    this.loadingGoogle = true;
    try {
      const payload = await this.firebaseAuth.signInWithGoogle();
      if (!payload) {
        this.loadingGoogle = false;
        if (this.firebaseAuth.usesWebGoogleRedirectFlow()) {
          return;
        }
        await this.presentGoogleSignInError();
        return;
      }
      this.submitGoogleLoginPayload(payload);
    } catch (error: unknown) {
      this.loadingGoogle = false;
      if (this.firebaseAuth.isUserCancelledGoogleSignIn(error)) {
        return;
      }
      if (this.firebaseAuth.isGoogleSignInDeveloperConfigError(error)) {
        console.error("Google sign-in error:", error);
        await this.presentGoogleSignInDeveloperError();
        return;
      }
      console.error("Google sign-in error:", error);
      await this.firebaseAuth.signOut();
      await this.presentGoogleSignInError();
    }
  }

  private async presentGoogleSignInDeveloperError(): Promise<void> {
    const header = this.translate.instant(
      "LOGIN_PAGE.GOOGLE_SIGNIN_DEVELOPER_ERROR_TITLE"
    );
    const message = this.translate.instant(
      "LOGIN_PAGE.GOOGLE_SIGNIN_DEVELOPER_ERROR"
    );
    const alert = await this.alertController.create({
      header,
      message,
      buttons: [this.translate.instant("BUTTONS.OK")],
    });
    await alert.present();
  }

  private async presentGoogleSignInError(): Promise<void> {
    const message = this.translate.instant("LOGIN_PAGE.GOOGLE_SIGNIN_FAILED");
    await this.bobToast.errorBottom(message, {
      title: this.translate.instant("BOB_TOAST.LOGIN_ERROR_TITLE"),
      duration: 5000,
    });
  }

  private async finishLogin(res: any): Promise<void> {
    const body = res?.body ?? res;
    if (!body?.access_token || !body?.user) {
      throw new Error("Login response missing access_token or user");
    }

    this.submitted = false;
    this.isLoading = false;

    const token = body.access_token;
    const refresh = body.refresh_token;
    const user = {
      ...body.user,
      roles: (body.user.roles ?? []).map((r: any) =>
        typeof r === "string" ? r : r?.name
      ),
      current_state: PusherService.normalizeCurrentState(body.user.current_state),
    };
    const rawRole = body.user?.roles?.[0]?.name ?? body.user?.roles?.[0] ?? "client";
    this.role = this.normalizeRole(rawRole);
    this.localStorageService.saveAuthDataFromLogin(
      token,
      user,
      this.role,
      refresh
    );

    const route = this.resolvePostLoginRoute(this.role);
    this.navigateWithReplace(route);
    this.localStorageService.removebackUrl();

    if (this.isClientRole(this.role) || this.isProviderRole(this.role)) {
      this.messagesService.initUnreadConversation();
    }

    void this.runPostLoginServices(user);
    this.accountCompletionOrchestrator.scheduleCompletionGates();
  }

  private resolvePostLoginRoute(role: string): string {
    const backUrl = this.localStorageService.getBackUrl();
    if (backUrl) {
      const urlRole = backUrl.split("/")[1];
      const goBack = urlRole === "particulier" ? "client" : "prestataire";
      if (goBack === this.normalizeRole(role)) {
        return backUrl.startsWith("/") ? backUrl.slice(1) : backUrl;
      }
    }
    if (this.isProviderRole(role)) {
      return "prestataire";
    }
    return "particulier";
  }

  private runPostLoginServices(user: { id: number }): void {
    const location = this.localStorageService.getLocation();
    if (location) {
      this.sendLocation(location, user.id);
    }

    this.authService.getCompletetion(this.role).subscribe((completionRes: any) => {
      this.appService.setUserCompletion(completionRes.body.profile_completion);
    });
    this.isLoading = false;
    this.pushNotificationService.initFcmPusher({ forceRefresh: true });
    if (this.isProviderRole(this.role)) {
      void this.backgroundLocationService.startPresencePing();
    }
    this.navigationService.pusherAuth();
    this.navigationService.subscribeToRoleChannel();
    this.navigationService.subscribeToDiscussionChannel();
  }

  navigateWithReplace(url: string) {
    this.navigationService.replace(url);

  }

  private normalizeRole(role: string): string {
    const value = String(role || "").toLowerCase().trim();
    if (value === "provider" || value === "prestataire") {
      return "prestataire";
    }
    if (value === "client" || value === "particulier") {
      return "client";
    }
    return value || "client";
  }

  private isClientRole(role: string): boolean {
    return this.normalizeRole(role) === "client";
  }

  private isProviderRole(role: string): boolean {
    return this.normalizeRole(role) === "prestataire";
  }

  async goBack() {
    const redirected = await this.redirectAuthenticatedUserFromLogin();
    if (redirected) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      this.navigateWithReplace("particulier");
      return;
    }

    this.navigationService.smartBack('/particulier');
  }
  onRememberMeChange(event: any) {
    this.rememberMe = event.detail.checked;
  }
  deleteSavedAccount(account: any, event: any) {
    event.stopPropagation();
    this.authService.deleteSavedAccount(account.email);
    this.savedAccounts = this.savedAccounts.filter(acc => acc.email !== account.email);
  }
  onSelectAccount(account: any) {
    if(account.loading || this.isLoading || this.loadingConnectAccount){
      return;
    }
    account.loading = true;
    this.loadingConnectAccount = true;
    this.login(account.email, account.password);   
  }

}
